// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * One-click light/dark switch for the app bar and the signed-out screens. It sets the same
 * per-device preference as Settings → Appearance, which still offers "System".
 */
import { Icon, resolvedTheme } from "@granite/ui";
import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { t } from "../lib/i18n.js";
import { preferences, setPreference } from "../lib/preferences.js";

export function ThemeToggle({ className = "" }: { className?: string }) {
  const theme = useStore(preferences, (p) => p.theme);
  const [effective, setEffective] = useState(() => resolvedTheme());
  useEffect(() => {
    setEffective(resolvedTheme());
    if (theme !== "system") return;
    // Follow the OS while the preference is "system".
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      setEffective(resolvedTheme());
    };
    mq.addEventListener("change", onChange);
    return () => {
      mq.removeEventListener("change", onChange);
    };
  }, [theme]);
  const next = effective === "dark" ? "light" : "dark";
  const label = next === "light" ? t("theme.switchToLight") : t("theme.switchToDark");
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-surface-muted hover:text-fg ${className}`}
      onClick={() => {
        setPreference("theme", next);
        setEffective(next);
      }}
    >
      <Icon name={next === "light" ? "sun" : "moon"} />
    </button>
  );
}
